import type {
  AdminExtensionRenderer,
  RouteResolution,
} from './admin-extension-contract'
import {
  asNonEmptyString,
  normalizeRoutePath,
} from './admin-extension-contract'
import { logAdminExtensionDiagnostic } from './admin-extension-diagnostics'
import {
  bumpAdminExtensionRegistryRevision,
  emitAdminExtensionLifecycleEvent,
  emitAdminExtensionPluginLifecycle,
} from './admin-extension-lifecycle'
import {
  clearAdminExtensionStore,
  clearRouteResolutionDiagnostics,
  removeOwnedByPluginId,
  routeById,
  routeByKey,
  routeByPath,
  sidebarItemById,
  sidebarSectionById,
  unresolvedRouteKeyDiagnostics,
  unresolvedRoutePathDiagnostics,
} from './admin-extension-store'
import { registerAdminExtension as registerAdminExtensionInternal } from './admin-extension-registration'
import { frontendDiagnosticsEvents } from '@emporion/ui'
export type {
  AdminExtensionRegistration,
  AdminExtensionRenderer,
  AdminExtensionRouteRegistration,
  AdminExtensionSidebarItemRegistration,
  AdminExtensionSidebarSectionRegistration,
  AdminExtensionLifecycleEvent,
  AdminExtensionLifecycleEventType,
  AdminExtensionDiagnosticPayload,
  AdminExtensionDiagnosticEvent,
} from './admin-extension-contract'
export { subscribeAdminExtensionDiagnostics } from './admin-extension-diagnostics'
export {
  getAdminExtensionRegistryRevision,
  subscribeAdminExtensionLifecycleEvents,
  subscribeAdminExtensionRegistryInvalidation,
} from './admin-extension-lifecycle'

export const unregisterAdminExtensionByPluginId = (pluginId: string) => {
  const pluginIdNormalized = asNonEmptyString(pluginId)
  if (!pluginIdNormalized) return
  const removed = removeOwnedByPluginId(pluginIdNormalized)
  if (!removed) return

  clearRouteResolutionDiagnostics()
  bumpAdminExtensionRegistryRevision()
  logAdminExtensionDiagnostic(frontendDiagnosticsEvents.adminExtension.disposed, 'Admin extension disposed', {
    pluginId: pluginIdNormalized,
    sections: removed.removedSections,
    items: removed.removedItems,
    routes: removed.removedRoutes,
  })
  emitAdminExtensionLifecycleEvent({
    type: 'extension:disposed',
    pluginId: pluginIdNormalized,
    timestamp: Date.now(),
    sections: removed.removedSections,
    items: removed.removedItems,
    routes: removed.removedRoutes,
  })
}

export const clearAdminExtensionRegistry = () => {
  clearAdminExtensionStore()
  bumpAdminExtensionRegistryRevision()
}

export const registerAdminExtension = (
  input: Parameters<typeof registerAdminExtensionInternal>[0],
): (() => void) => registerAdminExtensionInternal(input, unregisterAdminExtensionByPluginId)

export const getRegisteredAdminExtensionSidebarSections = () =>
  Array.from(sidebarSectionById.values())

export const getRegisteredAdminExtensionSidebarItems = () =>
  Array.from(sidebarItemById.values())

export const getRegisteredAdminExtensionRoutes = () =>
  Array.from(routeById.values())

export const resolveAdminExtensionRouteByKey = (key: string): RouteResolution | null => {
  const resolved = routeByKey.get(key)
  if (!resolved) {
    if (key.startsWith('ext:') && !unresolvedRouteKeyDiagnostics.has(key)) {
      unresolvedRouteKeyDiagnostics.add(key)
      logAdminExtensionDiagnostic(frontendDiagnosticsEvents.adminExtension.routeResolutionFailed, 'Admin extension route key resolution failed', {
        key,
      })
    }
    return null
  }
  return { key: resolved.key, routePath: resolved.routePath }
}

export const resolveAdminExtensionRouteByPath = (pathname: string): RouteResolution | null => {
  const normalized = normalizeRoutePath(pathname)
  const resolved = routeByPath.get(normalized)
  if (!resolved) {
    if (normalized.startsWith('/admin/extensions') && !unresolvedRoutePathDiagnostics.has(normalized)) {
      unresolvedRoutePathDiagnostics.add(normalized)
      logAdminExtensionDiagnostic(frontendDiagnosticsEvents.adminExtension.routeResolutionFailed, 'Admin extension route path resolution failed', {
        routePath: normalized,
      })
    }
    return null
  }
  return { key: resolved.key, routePath: resolved.routePath }
}

export const resolveAdminExtensionRequiredCapabilities = (sectionKey: string): string[] | null => {
  const resolved = routeByKey.get(sectionKey)
  if (!resolved) return null
  return [...resolved.requiredCapabilities]
}

export const isRegisteredAdminExtensionSectionKey = (sectionKey: string): boolean =>
  routeByKey.has(sectionKey)

export const resolveAdminExtensionRenderer = (sectionKey: string): AdminExtensionRenderer | null =>
  routeByKey.get(sectionKey)?.renderer ?? null

export const notifyAdminExtensionPluginEnabled = (pluginId: string) => {
  const pluginIdNormalized = asNonEmptyString(pluginId)
  if (!pluginIdNormalized) return
  emitAdminExtensionPluginLifecycle('plugin:enabled', pluginIdNormalized)
}

export const notifyAdminExtensionPluginDisabled = (pluginId: string) => {
  unregisterAdminExtensionByPluginId(pluginId)
  const pluginIdNormalized = asNonEmptyString(pluginId)
  if (!pluginIdNormalized) return
  emitAdminExtensionPluginLifecycle('plugin:disabled', pluginIdNormalized)
}

export const notifyAdminExtensionPluginReloaded = (pluginId: string) => {
  const pluginIdNormalized = asNonEmptyString(pluginId)
  if (!pluginIdNormalized) return
  emitAdminExtensionPluginLifecycle('plugin:reloaded', pluginIdNormalized)
}

export const runAdminExtensionRegistryHmrDispose = () => {
  clearAdminExtensionRegistry()
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    runAdminExtensionRegistryHmrDispose()
  })
}
